var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import classnames from 'classnames';
import { Popover } from '../Popover';
import { ActionMenuGroup } from './ActionMenuGroup';
import { ActionMenuItem } from './ActionMenuItem';
import { useArrowNav } from '../../hooks/useArrowNav';
import { useLatestEvent } from './useLatestEvent';
export const ActionMenu = (_a) => {
    var { children, className, direction = 'br', focusTrapOptions, padding = null, // Disable Popover's default padding
    wrapperClassName, width = 's', open: openProp, trigger: triggerProp, onClickOutside } = _a, rest = __rest(_a, ["children", "className", "direction", "focusTrapOptions", "padding", "wrapperClassName", "width", "open", "trigger", "onClickOutside"]);
    const menuContext = React.useContext(ActionMenuContext);
    const isNested = () => (menuContext === null || menuContext === void 0 ? void 0 : menuContext.length) && (menuContext === null || menuContext === void 0 ? void 0 : menuContext.length) > 0;
    const isControlled = typeof openProp !== 'undefined';
    const [internalOpen, setInternalOpen] = React.useState(false);
    const open = isControlled ? openProp : internalOpen;
    const handleOpenToggle = (newValue) => {
        if (!isControlled) {
            setInternalOpen(typeof newValue === 'boolean' ? newValue : (prev) => !prev);
        }
    };
    const handleClickOutside = () => {
        if (!isControlled) {
            setInternalOpen(false);
        }
        onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside();
    };
    const trigger = typeof triggerProp === 'function'
        ? triggerProp({
            isOpen: open,
            toggle: handleOpenToggle,
        })
        : triggerProp;
    const handleArrowKeys = (e, data) => {
        var _a, _b, _c, _d, _e;
        const cascadingItem = (_b = (_a = data.active) === null || _a === void 0 ? void 0 : _a.classList) === null || _b === void 0 ? void 0 : _b.contains('ActionMenuItem--cascading');
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight')
            data.disableOnce(); // Prevent focus move on left-right or when focus is not visible yet (no keyboard interaction)
        if (e.key === 'ArrowLeft' && isNested())
            (_c = rest.onClickOutside) === null || _c === void 0 ? void 0 : _c.call(rest, e); // Close ActionMenu, if nested
        if (e.key === 'ArrowRight' && cascadingItem)
            (_e = (_d = data.active) === null || _d === void 0 ? void 0 : _d.click) === null || _e === void 0 ? void 0 : _e.call(_d); // Open nested
    };
    const containerRef = useArrowNav({
        disabled: !rest.open,
        onKeyDown: handleArrowKeys,
    });
    const latestEvent = useLatestEvent();
    React.useEffect(() => {
        const el = containerRef.current;
        const setFocus = () => {
            var _a, _b;
            if (!latestEvent.current)
                return; // Latest event is undefined yet
            if (((_a = latestEvent.current) === null || _a === void 0 ? void 0 : _a.type) === 'keydown') {
                (_b = el.querySelector('button:not([disabled])')) === null || _b === void 0 ? void 0 : _b.focus(); // Keyboard event ? set focus first item
            }
            else {
                el.tabIndex = 0;
                el.focus(); // Pointer event ? set focus wrapper
                el.addEventListener('blur', () => el.removeAttribute('tabIndex'));
            }
        };
        if (rest.open && el)
            Promise.resolve().then(setFocus);
    }, [rest.open]); // eslint-disable-line
    return (React.createElement(Popover, Object.assign({ className: classnames(className, 'ActionMenu__OverlayWrapper'), focusTrapOptions: Object.assign({ autoFocus: false, autoFocusContainer: false, focusLock: false, restoreFocus: true }, focusTrapOptions), wrapperClassName: classnames(wrapperClassName, 'ActionMenu__TriggerWrapper'), popoverContentClassName: "ActionMenu", direction: direction, padding: padding, portal: true, width: width, "data-anvil-component": "ActionMenu", trigger: trigger, open: open, onClickOutside: handleClickOutside }, rest),
        React.createElement(ActionMenuContext.Provider, { value: menuContext.concat({
                itemsContainerRef: containerRef,
                onClickOutside: rest === null || rest === void 0 ? void 0 : rest.onClickOutside,
            }) },
            React.createElement("div", { className: "ActionMenu__ItemsWrapper", ref: containerRef }, children))));
};
ActionMenu.Group = ActionMenuGroup;
ActionMenu.Item = ActionMenuItem;
export const ActionMenuContext = React.createContext([]);
//# sourceMappingURL=ActionMenu.js.map